import { useState, useEffect } from 'react'
import type { AccessibilityPreferences } from '../types'

const STORAGE_KEY = 'olecio-accessibility-preferences'

const defaultPreferences: AccessibilityPreferences = {
  dyslexicFont: false,
  highContrast: false
}

export const useAccessibility = () => {
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(defaultPreferences)

  // Charger les préférences depuis localStorage au montage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setPreferences(parsed)
        applyPreferences(parsed)
      } catch (error) {
        console.error('Error parsing accessibility preferences:', error)
      }
    }
  }, [])

  // Appliquer les préférences au DOM
  const applyPreferences = (prefs: AccessibilityPreferences) => {
    const body = document.body
    
    // Police dyslexique
    if (prefs.dyslexicFont) {
      body.classList.add('dyslexic-font')
    } else {
      body.classList.remove('dyslexic-font')
    }

    // Contraste élevé
    if (prefs.highContrast) {
      body.classList.add('high-contrast')
    } else {
      body.classList.remove('high-contrast')
    }
  }

  // Sauvegarder et appliquer les préférences
  const updatePreferences = (newPreferences: Partial<AccessibilityPreferences>) => {
    const updated = { ...preferences, ...newPreferences }
    setPreferences(updated)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    applyPreferences(updated)
  }

  // Toggle pour la police dyslexique
  const toggleDyslexicFont = () => {
    updatePreferences({ dyslexicFont: !preferences.dyslexicFont })
  }

  // Toggle pour le contraste élevé
  const toggleHighContrast = () => {
    updatePreferences({ highContrast: !preferences.highContrast })
  }

  return {
    preferences,
    updatePreferences,
    toggleDyslexicFont,
    toggleHighContrast
  }
}
